import React, { useState, useEffect } from 'react';
import { X, MapPin, Save, AlertCircle, CheckCircle } from 'lucide-react';

const VenueManager = ({ 
  selectedSchedule, 
  availableGrounds, 
  schedulesService, 
  onScheduleUpdate,
  onClose 
}) => {

  console.log('🏟️ Available Grounds Structure:', availableGrounds);
  console.log('🏟️ First Ground:', availableGrounds[0]);

  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Debug: Log when selectedSchedule prop changes
  useEffect(() => {
    console.log('🔄 VenueManager: selectedSchedule prop updated', {
      scheduleId: selectedSchedule?.id,
      scheduleData: selectedSchedule?.schedule_data,
       firstGame: selectedSchedule?.schedule_data?.[0]?.games?.[0],
    firstGameGroundId: selectedSchedule?.schedule_data?.[0]?.games?.[0]?.ground_id,
    firstGameGroundName: selectedSchedule?.schedule_data?.[0]?.games?.[0]?.ground_name,
      timestamp: new Date().toISOString()
    });
  }, [selectedSchedule]);

  // Reset changes when schedule prop updates (after save)
useEffect(() => {
  if (Object.keys(changes).length === 0) {
    // Changes were just cleared (after save), force component to use fresh prop data
    console.log('🔄 Changes cleared, using fresh schedule data');
  }
}, [selectedSchedule, changes]);

  const handleChange = (weekIndex, gameIndex, groundId) => {
    console.log('📝 Change recorded:', { weekIndex, gameIndex, groundId });
    setChanges((prev) => ({ ...prev, [`${weekIndex}-${gameIndex}`]: groundId }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      console.log('💾 Starting save process...');
      console.log('📊 Changes to save:', changes);
      console.log('📋 Original schedule data:', selectedSchedule.schedule_data);
      
      const updated = JSON.parse(JSON.stringify(selectedSchedule.schedule_data));
      
 Object.entries(changes).forEach(([key, groundId]) => {
  const [wi, gi] = key.split('-').map(Number);
  const ground = availableGrounds.find((g) => g.id === groundId);
  
  console.log(`🎯 Updating game at week ${wi}, game ${gi}:`, {
    oldGroundId: updated[wi].games[gi].ground_id,
    newGroundId: groundId,
    groundFound: !!ground,
    groundDetails: ground ? {
      id: ground.id,
      name: ground.name,
      city: ground.city,
      state: ground.state
    } : null
  });
  
if (ground) {
  const updatedGame = {
    ...updated[wi].games[gi],
    ground_id: ground.id,
    ground_name: ground.ground_name || ground.name,
    ground_city: ground.city,
    ground_state: ground.state,
    ground_address_1: ground.address_1,
    ground_address_2: ground.address_2,
    ground_zip_code: ground.zip_code,
    ground_country: ground.country
  };
  
  console.log('📝 Updated game object:', updatedGame);
  updated[wi].games[gi] = updatedGame;
}
});

console.log('📦 Final updated schedule data:', updated);
      
      console.log('📤 Sending update to API:', {
        scheduleId: selectedSchedule.id,
        updatedData: updated
      });
      
      const apiResponse = await schedulesService.update(selectedSchedule.id, {
        venueAssignments: true,
        scheduleData: updated
      });
      
      console.log('✅ API response:', apiResponse);
      
      // Clear changes first
      console.log('🧹 Clearing local changes...');
      setChanges({});
      
      // Wait for parent to refresh data
      console.log('🔄 Calling onScheduleUpdate...');
      await onScheduleUpdate();
      console.log('✅ onScheduleUpdate completed');
      
      // Show success after data is refreshed
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      
      console.log('🎉 Save process completed successfully');
    } catch (err) {
      console.error('❌ Error during save:', err);
      setError('Failed to update venues. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div style={{
      width: '100%',
      height: '600px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      position: 'relative'
    }}>
      {/* Debug Info Panel */}
      <div style={{
        position: 'absolute',
        top: '60px',
        right: '20px',
        backgroundColor: '#1e293b',
        color: '#f1f5f9',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 1000,
        maxWidth: '300px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div>Schedule ID: {selectedSchedule?.id}</div>
        <div>Pending changes: {Object.keys(changes).length}</div>
        <div>Last updated: {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: '500' }}>Venues updated successfully!</span>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MapPin size={24} color="#3b82f6" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Venue Manager</h2>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px'
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Instruction */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#eff6ff',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#1e40af'
      }}>
        Assign venues to games. Changes are saved when you click "Save Changes".
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          margin: '16px 20px 0',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
      }}>
        <div style={{ display: 'grid', gap: '24px' }}>
          {selectedSchedule.schedule_data
            .sort((a, b) => parseInt(a.week) - parseInt(b.week))
            .map((week, wi) => (
            <div key={wi} style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '16px'
            }}>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1e293b'
              }}>
                Week {week.week}
              </h3>
              
<div style={{ display: 'grid', gap: '8px' }}>
  {week.games.map((game, gi) => {
    const key = `${wi}-${gi}`;
    const currentValue = changes[key] || game.ground_id || '';
    const hasChange = changes[key] !== undefined;
    
    // Get the display name for current venue
    const currentGround = availableGrounds.find(g => g.id === game.ground_id);
    const currentVenueName = currentGround 
      ? `${currentGround.ground_name} - ${currentGround.city}, ${currentGround.state}`  // ← CORRECT
      : 'No venue';
    
    return (
      <div key={gi} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: hasChange ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {game.homeTeam} vs {game.awayTeam}
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                            Current: {currentVenueName}
                        </div>
                      </div>
                      
                      <select
                        value={currentValue}
                        onChange={(e) => handleChange(wi, gi, e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">No venue assigned</option>
                        {availableGrounds.map((ground) => (
                          <option key={ground.id} value={ground.id}>
                            {ground.name} - {ground.city}, {ground.state}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer with Save Button */}
      {hasChanges && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: saving ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!saving) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (!saving) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            <Save size={16} />
            {saving ? 'Saving...' : `Save Changes (${Object.keys(changes).length})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default VenueManager;